// @ts-ignore
import router from '@/router';
import { useAssetStore } from '@/stores/useAssetStore';
import type { NotificationRow } from '@/types/notifications';
import { normalizeNotificationRow } from '@/types/notifications';
import type { Router } from 'vue-router';

export interface ResolvedAction {
  routeName: string;
  params: Record<string, string>;
}

export function resolveAction(n: NotificationRow): ResolvedAction | null {
  const normalized = normalizeNotificationRow(
    n as unknown as { data: unknown } & NotificationRow,
  ) as NotificationRow;

  if (normalized.entity_type === 'asset' || normalized.entity_type === 'companion') {
    const payload = normalized.data as unknown as {
      node_id?: string;
      asset_id?: string;
      device_id?: string;
    };

    const nodeId = String(payload?.node_id ?? '');
    const assetId = String(payload?.asset_id ?? '');
    const deviceId = String(payload?.device_id ?? '');

    if (!nodeId || !assetId || !deviceId) {
      console.error(
        'Missing identifiers for resolveAction',
        JSON.stringify({ entity_type: normalized.entity_type, data: normalized.data }, null, 2),
      );
      return null;
    }
    return {
      routeName: 'asset-preview',
      params: { node_id: nodeId, asset_id: assetId, device_id: deviceId },
    };
  }
  if (n.entity_type === 'system') {
    return { routeName: 'map', params: {} };
  }
  return null;
}

/** Result of navigation attempt - distinguishes success, already-on-page, or error cases */
export interface NavigationResult {
  navigated: boolean; // True if router changed pages
  alreadyOnPage: boolean; // True if user was already on target page
  error?: string; // Error message if navigation failed
}

/**
 * Navigates to notification target. Checks if user is already on the page to avoid unnecessary navigation.
 * Returns structured result to allow UI to show appropriate feedback (toast when already on page).
 */
export async function goToNotificationTarget(n: NotificationRow): Promise<NavigationResult> {
  const typedRouter = router as Router;
  const res = resolveAction(n);
  if (!res) {
    return { navigated: false, alreadyOnPage: false, error: 'No action resolved' };
  }

  // Check if already on target page (compare route name + all params)
  const currentRoute = typedRouter.currentRoute.value;
  if (currentRoute.name === res.routeName) {
    const currentParams = currentRoute.params;
    const targetParams = res.params;
    const paramKeys = new Set([...Object.keys(currentParams), ...Object.keys(targetParams)]);

    let paramsMatch = true;
    for (const key of paramKeys) {
      if (String(currentParams[key] || '') !== String(targetParams[key] || '')) {
        paramsMatch = false;
        break;
      }
    }

    if (paramsMatch) {
      return { navigated: false, alreadyOnPage: true };
    }
  }

  // Defensive navigation: if the target is an asset/companion preview, verify it still exists
  // before routing to avoid runtime errors in the destination view.
  if (res.routeName === 'asset-preview') {
    const { node_id, asset_id, device_id } = res.params as unknown as {
      node_id: string;
      asset_id: string;
      device_id: string;
    };
    try {
      const assetStore = useAssetStore();
      // Ensure assets are loaded
      if (!assetStore.allAssets.length) {
        await assetStore.fetchAssets();
      }
      const asset = assetStore.getAssetById(node_id, asset_id, device_id);
      if (!asset) {
        // Asset no longer exists or is not accessible: route to a safe page
        try {
          await typedRouter.push({ name: 'asset-not-found' });
        } catch (routeError) {
          console.error('Failed to navigate to asset-not-found route:', routeError);
          await typedRouter.push({ name: 'notfound' });
        }
        return { navigated: true, alreadyOnPage: false };
      }
    } catch (err) {
      // On unexpected errors, also route to a safe page to prevent crashes
      try {
        await typedRouter.push({ name: 'notifications' });
      } catch (routeError) {
        console.error('Failed to navigate to notifications route:', routeError);
        await typedRouter.push({ name: 'notfound' });
      }
      return { navigated: true, alreadyOnPage: false };
    }
  }

  try {
    await typedRouter.push({ name: res.routeName as unknown as string, params: res.params });
    return { navigated: true, alreadyOnPage: false };
  } catch (routeError) {
    console.error('Failed to navigate to route:', res.routeName, routeError);
    await typedRouter.push({ name: 'notfound' });
    return { navigated: true, alreadyOnPage: false, error: 'Navigation failed' };
  }
}
